import { Content, isFilled } from '@prismicio/client';
import {
  PrismicLink,
  PrismicRichText,
  SliceComponentProps
} from '@prismicio/react';
import { crush } from '@tedconf/js-crushinator-helpers';
import cx from 'classnames';
import { isEmpty } from 'lodash-es';
import { useContext, type JSX } from 'react';
import { useIntl } from 'react-intl';

import { Video, VideosBySlugQuery } from 'api';
import PrismicInlineText from 'components/PRISMIC/PrismicInlineText';
import styles from 'components/PRISMIC/scss/pages.module.scss';
import { PageBuilderHeading } from 'components/PRISMIC/slices/Heading/Heading';
import { StaticPropsContext } from 'components/pages/prismic/Page';
import { PageLayoutContext } from 'components/pages/prismic/Page/PageContent';
import getImageFromSet from 'lib/get-image-from-set';
import { formatAsTimestamp } from 'lib/util';

/**
 * Props for `TalksFeed`.
 */
export type TalksFeedProps = SliceComponentProps<Content.TalksFeedSlice>;

/**
 * Function To Reorder Talks.
 */
const reorderedTalks = (order: string[], talks: Video[]) => {
  const tempMap = talks.reduce(
    (accum, item) => {
      if (item?.slug) {
        accum[item.slug] = item;
      }
      return accum;
    },
    {} as Record<string, Video>
  );
  return order.map(slug => tempMap[slug]);
};

/**
 * Component for "TalksFeed" Slices.
 */
const TalksFeed = ({ slice }: TalksFeedProps): JSX.Element | null => {
  const staticProps = useContext(StaticPropsContext);
  const columnWidth: number = useContext(PageLayoutContext);
  const floatImages = columnWidth > 1 / 3;
  const { formatDate, formatNumber } = useIntl();
  // Get Talks from StaticProps from Prismic
  // These talks can come back in any order
  const talks = staticProps[slice.id] as NonNullable<
    VideosBySlugQuery['videos']
  >['nodes'];
  const noTalks = isEmpty(talks);

  // Short Circuit if talks are null
  if (noTalks) return null;

  // Reorder talks according to how they were entered in the interface.
  const talkOrder = slice.items.map(item => item.slug);
  const orderedTalks = reorderedTalks(talkOrder as string[], talks! as Video[]);

  // Slice Component Render
  return (
    <section
      className={styles['pages-module']}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <PageBuilderHeading level="default">
        <PrismicRichText field={slice.primary.heading} />
      </PageBuilderHeading>

      <div className={cx(styles.list, styles.m3)}>
        {orderedTalks!
          .filter(it => it)
          .map(talk => (
            <article
              key={talk!.id}
              className={cx(
                styles['list__item'],
                styles.media,
                styles['media--oh-v'],
                styles['media--xs-h'],
                floatImages ? '' : styles['media--lg-v']
              )}
            >
              {slice.primary.show_thumbnails && (
                <div
                  className={cx(
                    styles['media__image'],
                    styles['media__image--thumb']
                  )}
                >
                  <a href={'/talks/' + talk!.slug}>
                    <div
                      className={cx(
                        styles.thumb,
                        styles['thumb--video'],
                        styles['thumb--crop-top']
                      )}
                    >
                      <span className={styles.thumb__sizer}>
                        <span className={styles.thumb__tugger}>
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            className={cx(styles['thumb__image'], 'cover-full')}
                            src={
                              crush(
                                getImageFromSet(
                                  talk!.primaryImageSet || [],
                                  '16x9'
                                ) || '',
                                {
                                  width: 320,
                                  quality: 89
                                }
                              ) || ''
                            }
                            alt={talk!.title || ''}
                          />
                          <span className={styles['thumb__aligner']} />
                        </span>
                      </span>
                      {talk!.duration && (
                        <span className={styles.thumb__duration}>
                          {formatAsTimestamp(talk!.duration)}
                        </span>
                      )}
                    </div>
                  </a>
                </div>
              )}

              <div className={styles.media__message}>
                {slice.primary.show_speaker_names && (
                  <h4 className={styles.h12}>{talk!.presenterDisplayName}</h4>
                )}
              </div>

              <h4 className={cx(styles.h9, styles.m5)}>
                <a href={'/talks/' + talk!.slug}>{talk!.title}</a>
              </h4>

              <div className={styles.meta}>
                {slice.primary.show_video_view_counts &&
                  talk!.viewedCount > 0 && (
                    <span className={styles.meta__item}>
                      <span className={styles.meta__val}>
                        {formatNumber(talk!.viewedCount, {
                          notation: 'compact',
                          compactDisplay: 'short'
                        })}{' '}
                        views{' '}
                      </span>
                    </span>
                  )}
                {slice.primary.show_video_durations && talk!.publishedAt && (
                  <span className={styles.meta__item}>
                    <span className={styles.meta__val}>
                      {' '}
                      {formatDate(talk!.publishedAt, {
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                  </span>
                )}
              </div>
            </article>
          ))}
      </div>
      {isFilled.link(slice.primary.link) && (
        <PrismicLink field={slice.primary.link} className={styles.l3}>
          <PrismicInlineText field={slice.primary.link_text} />
        </PrismicLink>
      )}
    </section>
  );
};

export default TalksFeed;
